import {
  Business as BusinessIcon,
  Category as CategoryIcon,
  CheckCircle as CheckCircleIcon,
  Home as HomeIcon
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Chip,
  Fade,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useSelector } from "react-redux";
import { getMyServices } from "../utils";
import { WidgetEmployee } from "./common/WidgetEmployee";

export const IndividualServices = ({
  title,
  type,
  sxStyle = {},
  onChange,
  swapyName,
}) => {
  const token = sessionStorage.getItem("token");
  const [loading, setLoading] = useState(false);
  const { showBoundary } = useErrorBoundary();
  const [myServices, setMyServices] = useState([]);
  const [isVisible, setIsVisible] = useState(false);

  const { company } = useSelector(state => state.userState)
  const companyId = company?.id ?? null;
  const isResidential = type === "residential";

  useEffect(() => {
    const timeout = setTimeout(() => setIsVisible(true), 200);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    let alive = true;

    const fetchServices = async () => {
      try {
        setLoading(true);
        const list = await getMyServices(type, token, companyId);
        if (alive) setMyServices(Array.isArray(list) ? list : []);
      } catch (err) {
        showBoundary(err);
      } finally {
        if (alive) setLoading(false);
      }
    };

    fetchServices();
    return () => { alive = false; };
  }, [type, token, companyId, onChange, showBoundary]);


  const activeServices = useMemo(() => {
    const list = Array.isArray(myServices) ? myServices : [];
    return list.filter((s) => s?.companyServices?.[0]?.isActive === true);
  }, [myServices]);

  const ServiceSkeleton = () => (
    <Stack spacing={1} sx={{ p: 2 }}>
      {[...Array(3)].map((_, index) => (
        <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" width="70%" height={20} />
        </Box>
      ))}
    </Stack>
  );


  const EmptyState = () => (
    <Box
      sx={{
        p: 3,
        textAlign: "center",
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Avatar sx={{ width: 48, height: 48, backgroundColor: "var(--fx-surface-base, #FFFFFF)", mb: 2 }}>
        <CategoryIcon sx={{ fontSize: 24 }} />
      </Avatar>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        No active services
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, opacity: 0.7 }}>
        Click "Manage Services" to enable services
      </Typography>
    </Box>
  );

  const renderItemName = (service) =>
    service?.workServices?.name || service?.name || "Unnamed service";

  return (
    <WidgetEmployee swapyName={swapyName}>
      <Fade in={isVisible} timeout={400}>
        <Box
          sx={{
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            borderRadius: 3,
            overflow: "hidden",
            boxSizing: "border-box",
            ...sxStyle,
          }}
        >
          <Box sx={{ p: 2.5 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Avatar
                sx={{
                  bgcolor: isResidential ? "var(--fx-surface-muted, #F1F5F9)" : "#a2ddf8ff",
                  width: 40,
                  height: 40,
                }}
              >
                {isResidential ? (
                  <HomeIcon sx={{ fontSize: 20, color: "#2e7d32" }} />
                ) : (
                  <BusinessIcon sx={{ fontSize: 20, color: "#0277bd" }} />
                )}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "text.primary", lineHeight: 1.2 }}>
                  {title}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {activeServices?.length} service{activeServices?.length !== 1 ? "s" : ""} active
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
            {loading ? (
              <ServiceSkeleton />
            ) : activeServices?.length === 0 ? (
              <EmptyState />
            ) : (
              <Box sx={{ height: "100%", overflowY: "auto" }}>
                <List sx={{ py: 1 }}>
                  <Box sx={{ px: 2, pb: 0.5 }}>
                    <Typography variant="overline" sx={{ opacity: 0.7 }}>
                      Active
                    </Typography>
                  </Box>
                  {activeServices.map((service, idx) => (
                    <Fade
                      in
                      timeout={300}
                      style={{ transitionDelay: `${idx * 40}ms` }}
                      key={`active-${service.id ?? idx}`}
                    >
                      <ListItem sx={{ py: 1, px: 2 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon
                            sx={{ fontSize: 18, color: isResidential ? "#4caf50" : "#0277bd" }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
                              {renderItemName(service)}
                            </Typography>
                          }
                        />
                      </ListItem>
                    </Fade>
                  ))}
                </List>
              </Box>
            )}
          </Box>

          {activeServices?.length > 0 && (
            <Box sx={{ p: 1.5, textAlign: "center" }}>
              <Chip
                size="small"
                label={`${activeServices?.length} Active`}
                sx={{
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  height: 24,
                  "& .MuiChip-label": { px: 1.5 },
                }}
              />
            </Box>
          )}
        </Box>
      </Fade>
    </WidgetEmployee>
  );
};
